import type * as SentryNext from '@sentry/nextjs'

const SENTRY_DSN_ADMIN =
  'https://2f5347ae2129a8413098fbb4e74422af@o4510791720894464.ingest.us.sentry.io/4510791738851328'
const SENTRY_DSN_ACCOUNT =
  'https://d811d1abb4c2d60b3034149e8e7a3a20@o4510791720894464.ingest.us.sentry.io/4510797147537408'
const SENTRY_DSN_MARKETING =
  'https://9ff5609ba3361e0d7df740c3c42f7023@o4510791720894464.ingest.us.sentry.io/4510797153042432'
const SENTRY_DSN_TRIGGER =
  'https://b733ad5d1d76bcae561c5535216fed75@o4510791720894464.ingest.us.sentry.io/4510797157564416'
const SENTRY_DSN_CONTENT =
  'https://166cddf379316aec00f6d37c5dd4b2e8@o4510791720894464.ingest.us.sentry.io/4510797161824256'
const SENTRY_DSN_API = 'https://90daee6346512f39838295ad316292c4@o4510791720894464.ingest.us.sentry.io/4510797164904448'
const SENTRY_DSN_PARTNER =
  'https://e12fcda266a42bed77ceb0cc90265cd5@o4510791720894464.ingest.us.sentry.io/4510797167001600'
const SENTRY_DSN_SCHOOL =
  'https://d5652090dd30a99309c3fbeac552d918@o4510791720894464.ingest.us.sentry.io/4510797168640000'
const SENTRY_DSN_WEB = 'https://7a4a9f16e69d9b61339f4083c87924d3@o4510791720894464.ingest.us.sentry.io/4510797170212864'

/**
 * The HTTP status an error carries, if any. Fastify (including `@fastify/rate-limit`) and Better
 * Auth's `APIError` set `statusCode`; a `TRPCClientError` carries it in `data.httpStatus`.
 */
const httpStatusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) return undefined
  if ('statusCode' in error && typeof error.statusCode === 'number') return error.statusCode
  if ('data' in error && typeof error.data === 'object' && error.data !== null && 'httpStatus' in error.data) {
    return typeof error.data.httpStatus === 'number' ? error.data.httpStatus : undefined
  }
  return undefined
}

/**
 * A 4xx is the request being refused on purpose — wrong PIN, rate limit, already claimed — not a
 * bug, so it never goes to Sentry or PostHog Error Tracking. Every event counts against the quota.
 */
const isClientError = (error: unknown) => {
  const status = httpStatusOf(error)
  return status !== undefined && status >= 400 && status < 500
}

// Match the environment from your deployment.
// NOTE: reference process.env.* directly (not via the `env` alias) so Next.js
// inlines NEXT_PUBLIC_* and NODE_ENV into the client bundle at build time.
// Aliasing process.env defeats the static replacement and the browser always
// falls back to 'development'.
const sentryEnvironment = () => process.env.NEXT_PUBLIC_PROD_ENV ?? process.env.NODE_ENV ?? 'development'

const sentryOptions: (dsn: string) => Parameters<typeof SentryNext.init>[0] = (dsn) => ({
  dsn,

  environment: sentryEnvironment(),

  // Local development never reports — its errors are seen in the terminal, and they eat the quota.
  enabled: sentryEnvironment() !== 'development',

  // The learner's connection dropping (school wifi, a closed laptop) — not actionable. Chrome,
  // Safari and Firefox word it differently. Anchored so only the browser's own message matches.
  ignoreErrors: [/^Failed to fetch/, /^Load failed/, /^NetworkError when attempting to fetch resource/],

  beforeSend: (event, hint) => (isClientError(hint.originalException) ? null : event),

  // Match the release version used during source map upload
  release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? process.env.VERCEL_GIT_COMMIT_SHA,

  // Share of requests traced. Spans have their own quota, and at 1 every request and page view
  // spent it; a tenth still surfaces N+1 queries and slow endpoints.
  tracesSampleRate: 0.1,

  // Enable logs to be sent to Sentry
  enableLogs: true,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/node/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
})

const sentryNextJsConfig = ({
  project,
}: {
  project: 'admin' | 'account' | 'marketing' | 'partner' | 'school' | 'trigger' | 'web' | 'api' | 'content'
}): SentryNext.SentryBuildOptions => ({
  // For all available options, see:
  // https://www.npmjs.com/package/@sentry/webpack-plugin#options

  org: 'bacapibo',
  project,
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  authToken: process.env.SENTRY_AUTH_TOKEN!,

  // Match the release and environment with runtime config
  release: {
    name: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? process.env.VERCEL_GIT_COMMIT_SHA,
    deploy: {
      env: process.env.NEXT_PUBLIC_PROD_ENV ?? process.env.NODE_ENV ?? 'development',
    },
  },

  // Always silent to prevent hanging on log outputs
  silent: true,

  // For all available options, see:
  // https://docs.sentry.io/platforms/javascript/guides/nextjs/manual-setup/

  // Reduced to prevent OOM on Vercel, especially with MDX
  // Set to false to reduce memory usage during build
  widenClientFileUpload: false,

  // Disable telemetry to prevent hanging issues
  telemetry: false,

  // Disable source map uploads by default (Vercel builds)
  // Only enable when SENTRY_UPLOAD_SOURCEMAPS=true (GitHub Actions)
  sourcemaps: {
    disable: process.env.SENTRY_UPLOAD_SOURCEMAPS !== 'true',
  },

  // Route browser requests to Sentry through a Next.js rewrite to circumvent ad-blockers.
  // This can increase your server load as well as your hosting bill.
  // Note: Check that the configured route will not match with your Next.js middleware, otherwise reporting of client-
  // side errors will fail.
  tunnelRoute: '/monitoring',

  // Additional error handling to prevent build hangs
  errorHandler: (err: Error) => {
    console.error('Sentry source map upload warning:', err.message)
  },

  webpack: {
    // Automatically tree-shake Sentry logger statements to reduce bundle size
    treeshake: {
      removeDebugLogging: true,
    },
    // Enables automatic instrumentation of Vercel Cron Monitors. (Does not yet work with App Router route handlers.)
    // See the following for more information:
    // https://docs.sentry.io/product/crons/
    // https://vercel.com/docs/cron-jobs
    automaticVercelMonitors: false,
  },
})

export {
  isClientError,
  sentryEnvironment,
  sentryNextJsConfig,
  sentryOptions,
  SENTRY_DSN_ADMIN,
  SENTRY_DSN_ACCOUNT,
  SENTRY_DSN_MARKETING,
  SENTRY_DSN_PARTNER,
  SENTRY_DSN_SCHOOL,
  SENTRY_DSN_TRIGGER,
  SENTRY_DSN_WEB,
  SENTRY_DSN_CONTENT,
  SENTRY_DSN_API,
}
